import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Avatar,
  Link as MuiLink,
} from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import paymentdashboardservice from "../../services/paymentdashboardservice";
import { GoldStarDefs, StarPicker, ReadonlyStars } from "../common/StarRating";

const MIN_CHARS = 200;
const MAX_CHARS = 500;

// Same rating system as CreatorRemarksTab.jsx (the payment-history page's
// "team ratings" tab) — same model, same API, same ['influencer-ratings', id]
// query key, just a compact entry point so a rating can be given without
// leaving the plan. Only reachable when the row has a rapidInfluencerId
// (a plan-influencer not yet linked to a master record can't be rated,
// same restriction the payment-history link itself already has).
export default function InfluencerReviewModal({ open, onClose, creatorId, name, avatar }) {
  const { id: planId } = useParams();
  const qc = useQueryClient();
  const [myRating, setMyRating] = useState(0);
  const [comment, setComment] = useState("");
  const [saveError, setSaveError] = useState("");
  const didInit = useRef(false);

  const { data, isLoading } = useQuery({
    queryKey: ["influencer-ratings", creatorId],
    queryFn: () => paymentdashboardservice.getInfluencerRatings(creatorId),
    enabled: open && Boolean(creatorId),
    staleTime: 60_000,
  });

  // Reseed from server truth each time the modal opens for a (possibly
  // different) influencer, mirroring DeliverableColumnsMenu's same pattern.
  useEffect(() => {
    if (!open) {
      didInit.current = false;
      setSaveError("");
      return;
    }
    if (data && !didInit.current) {
      didInit.current = true;
      const mine = data.ratings?.find((r) => String(r.userId) === String(data.currentUserId));
      setMyRating(mine?.rating ?? 0);
      setComment(mine?.comment || "");
    }
  }, [data, open]);

  // The plan's own avgRating/totalRatings column is a batch aggregate
  // computed once per GET /:planId/summary (see buildRatingsSummaryMap in
  // routes/plans.js) — invalidate it too, not just this influencer's own
  // ratings query, so the table's Reviews column reflects the change too.
  const invalidateBoth = () => {
    qc.invalidateQueries({ queryKey: ["influencer-ratings", creatorId] });
    if (planId) qc.invalidateQueries({ queryKey: ["plan-summary", planId] });
  };

  // The API can respond 200 with { success: false, error } for a rejected
  // save (e.g. the influencer record was deleted/merged mid-edit) rather than
  // throwing — mirrors the same success-field check CreatorRemarksTab.jsx
  // already does, so a rejected save doesn't silently look like it worked.
  const saveMutation = useMutation({
    mutationFn: (payload) => paymentdashboardservice.saveInfluencerRating(creatorId, payload),
    onSuccess: (res) => {
      if (res?.success) {
        setSaveError("");
        invalidateBoth();
      } else {
        setSaveError(res?.error || "Failed to save. Please try again.");
      }
    },
    onError: () => setSaveError("Failed to save. Please try again."),
  });

  const deleteMutation = useMutation({
    mutationFn: () => paymentdashboardservice.deleteInfluencerRating(creatorId),
    onSuccess: (res) => {
      if (!res?.success) return;
      setMyRating(0);
      setComment("");
      invalidateBoth();
    },
  });

  const cc = comment.trim().length;
  const canSave = myRating > 0 && cc >= MIN_CHARS;
  const avg = data?.averageRating || 0;
  const total = data?.totalRatings || 0;
  const meId = data?.currentUserId;
  const mine = (data?.ratings || []).find((r) => String(r.userId) === String(meId));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
      <GoldStarDefs />
      <DialogTitle sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1.5 }}>
        <Avatar src={avatar} sx={{ width: 32, height: 32 }}>{name?.[0]?.toUpperCase()}</Avatar>
        Review {name}
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={26} sx={{ color: "#FF5200" }} />
          </Box>
        ) : (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
              <ReadonlyStars value={avg > 0 ? Math.round(avg) : 0} size={18} />
              <Typography variant="body2" color="text.secondary">
                {total > 0 ? `${avg.toFixed(1)} avg · ${total} review${total === 1 ? "" : "s"}` : "No reviews yet"}
              </Typography>
            </Box>

            <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", letterSpacing: 1 }}>
              YOUR RATING
            </Typography>
            <Box sx={{ my: 1.5 }}>
              <StarPicker value={myRating} onChange={setMyRating} />
            </Box>
            <TextField
              multiline
              rows={4}
              fullWidth
              placeholder={`Add a detailed note (required, min. ${MIN_CHARS} characters)…`}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              inputProps={{ maxLength: MAX_CHARS }}
              helperText={`${comment.length} / ${MAX_CHARS}${canSave ? " ✓" : ""}`}
            />
            {saveError && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {saveError}
              </Typography>
            )}
            {mine && (
              <Button
                size="small"
                color="error"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isLoading}
                sx={{ mt: 1, textTransform: "none" }}
              >
                Remove your rating
              </Button>
            )}
            <MuiLink
              href={`/payment-history/${creatorId}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: "block", mt: 1.5, fontSize: 12.5 }}
            >
              View all team reviews →
            </MuiLink>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", borderRadius: "10px" }}>
          Close
        </Button>
        <Button
          variant="contained"
          disabled={!canSave || saveMutation.isLoading}
          onClick={() => saveMutation.mutate({ rating: myRating, comment: comment.trim() })}
          sx={{ textTransform: "none", borderRadius: "10px", boxShadow: "none" }}
        >
          {saveMutation.isLoading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Save Review"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
